export const MAX_FILE_BYTES = 20 * 1024 * 1024;
export const MIN_SHORT_EDGE = 480;
export const WORKING_LONG_EDGE = 2048;
export const UPLOAD_LONG_EDGE = 1280;

export class ImageInputError extends Error {}

export interface LoadedPhoto {
  /** Working image, long edge <= 2048 px. Used for rendering and the final preview. */
  working: HTMLCanvasElement;
  /** Upload image, long edge <= 1280 px, JPEG 0.85. Sent to /api/segment. */
  upload: Blob;
}

function isHeic(file: File) {
  return /hei[cf]/i.test(file.type) || /\.(heic|heif)$/i.test(file.name);
}

async function decode(file: Blob): Promise<ImageBitmap> {
  return createImageBitmap(file, { imageOrientation: 'from-image' });
}

export function resizeToCanvas(src: CanvasImageSource & { width: number; height: number }, longEdge: number): HTMLCanvasElement {
  const scale = Math.min(1, longEdge / Math.max(src.width, src.height));
  const c = document.createElement('canvas');
  c.width = Math.round(src.width * scale);
  c.height = Math.round(src.height * scale);
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, c.width, c.height);
  return c;
}

export function canvasToJpeg(canvas: HTMLCanvasElement, quality = 0.85): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('JPEG encode failed'))), 'image/jpeg', quality),
  );
}

/** Decodes a user file with EXIF orientation applied, falling back to heic2any for HEIC/HEIF. */
export async function loadPhoto(file: File): Promise<LoadedPhoto> {
  if (file.size > MAX_FILE_BYTES) throw new ImageInputError('That photo is over 20 MB. Please choose a smaller one.');

  let bitmap: ImageBitmap;
  try {
    bitmap = await decode(file);
  } catch {
    if (!isHeic(file)) throw new ImageInputError("We couldn't open that file. Please choose a JPEG or PNG photo.");
    try {
      const { default: heic2any } = await import('heic2any');
      const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
      bitmap = await decode(Array.isArray(converted) ? converted[0] : converted);
    } catch {
      throw new ImageInputError("We couldn't open that HEIC photo. Please try a JPEG, or take a new photo.");
    }
  }

  if (Math.min(bitmap.width, bitmap.height) < MIN_SHORT_EDGE) {
    bitmap.close();
    throw new ImageInputError('That photo is too small. Please use one at least 480 pixels on the short side.');
  }

  const working = resizeToCanvas(bitmap, WORKING_LONG_EDGE);
  bitmap.close();
  const upload = await canvasToJpeg(resizeToCanvas(working, UPLOAD_LONG_EDGE), 0.85);
  return { working, upload };
}

/** Loads an image URL (samples) into a working canvas. */
export async function loadPhotoUrl(url: string): Promise<HTMLCanvasElement> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Image ${url}: ${res.status}`);
  const bitmap = await decode(await res.blob());
  const c = resizeToCanvas(bitmap, WORKING_LONG_EDGE);
  bitmap.close();
  return c;
}
